/**
 * Transaction-ledger chip color pairs (DNKADM-0269) — moved here from
 * `src/features/transaction-ledger/PaymentTransactionsTable.tsx` (the
 * per-payment-status chip tints, `STATUS_TINTS`) so the feature dir stays
 * literal-free (the DNKADM-0269 ESLint guard bans raw colors in
 * `src/features/**`; `src/theme/**` owns them).
 *
 * LIGHT values are byte-identical to the pre-0269 literals (the 0266 identity
 * constraint). DARK values follow the shared translucent-tint + pastel-ink
 * ramp used across the sibling `activity`/`configAudit` maps — the same
 * four-family palette (purple/green/amber/red) recurs across the three
 * because the underlying feeds share the same design-handoff chip colors.
 */

/** One status's chip tint/ink pair. */
interface PaymentStatusStyle {
  bg: string;
  fg: string;
}

export interface TransactionLedgerMaps {
  paymentStatus: Record<
    'pending' | 'succeeded' | 'failed' | 'requires_action' | 'requires_redirect',
    PaymentStatusStyle
  >;
  /** Unknown/unmapped payment status — same shape `StatusChip` falls back to. */
  paymentStatusFallback: PaymentStatusStyle;
}

const LIGHT: TransactionLedgerMaps = {
  paymentStatus: {
    pending: { bg: '#EDEAF6', fg: '#5B4B9E' },
    succeeded: { bg: '#E5F1E9', fg: '#2E7D52' },
    failed: { bg: '#F6E4E0', fg: '#C0492E' },
    requires_action: { bg: '#FBF0E0', fg: '#9A6A16' },
    requires_redirect: { bg: '#FBF0E0', fg: '#9A6A16' },
  },
  paymentStatusFallback: { bg: '#EDEAE4', fg: 'rgba(0,0,0,0.6)' },
};

const DARK: TransactionLedgerMaps = {
  paymentStatus: {
    pending: { bg: 'rgba(91,75,158,0.22)', fg: '#B3ABCB' },
    succeeded: { bg: 'rgba(46,125,82,0.24)', fg: '#7FD3A3' },
    failed: { bg: 'rgba(192,73,46,0.18)', fg: '#F0A090' },
    requires_action: { bg: 'rgba(216,154,46,0.16)', fg: '#E8C27A' },
    requires_redirect: { bg: 'rgba(216,154,46,0.16)', fg: '#E8C27A' },
  },
  paymentStatusFallback: { bg: 'rgba(184,192,186,0.18)', fg: '#B8C0BA' },
};

/** Per-mode transaction-ledger chip pairs. Light === the pre-0269 literals. */
export function buildTransactionLedgerMaps(mode: 'light' | 'dark'): TransactionLedgerMaps {
  return mode === 'dark' ? DARK : LIGHT;
}
